import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Divider
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { storage, db } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, serverTimestamp, updateDoc, collection } from 'firebase/firestore';

const SubmitVerificationPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentImage, setDocumentImage] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    nationality: '',
    documentNumber: '',
    expiryDate: ''
  });
  const [errors, setErrors] = useState({});
  const [userVerificationStatus, setUserVerificationStatus] = useState(null);
  
  // Check if user has already submitted a verification request
  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (!currentUser) return;
      
      try {
        const userDoc = await db.collection('users').doc(currentUser.uid).get();
        const userData = userDoc.data();
        
        if (userData) {
          setUserVerificationStatus({
            isVerified: userData.isVerified || false,
            verificationSubmitted: userData.verificationSubmitted || false,
            verificationRejected: userData.verificationRejected || false,
            verificationRejectedReason: userData.verificationRejectedReason || ''
          });
          
          // Pre-fill form with user data if available
          setFormData({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            birthDate: userData.birthDate || '',
            nationality: userData.nationality || '',
            documentNumber: '',
            expiryDate: ''
          });
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
        enqueueSnackbar('Erreur lors de la vérification du statut', { variant: 'error' });
      }
    };
    
    checkVerificationStatus();
  }, [currentUser, enqueueSnackbar]);
  
  // Handle document image selection
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file type
      if (!file.type.includes('image/')) {
        enqueueSnackbar('Veuillez sélectionner une image', { variant: 'error' });
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('L\'image ne doit pas dépasser 5MB', { variant: 'error' });
        return;
      }
      
      setDocumentImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!documentType) newErrors.documentType = 'Veuillez sélectionner un type de document';
    if (!documentImage) newErrors.documentImage = 'Veuillez télécharger une image de votre document';
    if (!formData.firstName.trim()) newErrors.firstName = 'Le prénom est requis';
    if (!formData.lastName.trim()) newErrors.lastName = 'Le nom est requis';
    if (!formData.birthDate) newErrors.birthDate = 'La date de naissance est requise';
    if (!formData.nationality.trim()) newErrors.nationality = 'La nationalité est requise';
    if (!formData.documentNumber.trim()) newErrors.documentNumber = 'Le numéro de document est requis';
    
    // Validate expiry date
    if (!formData.expiryDate) {
      newErrors.expiryDate = 'La date d\'expiration est requise';
    } else {
      const today = new Date();
      const expiryDate = new Date(formData.expiryDate);
      if (expiryDate < today) {
        newErrors.expiryDate = 'Le document est expiré';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      enqueueSnackbar('Veuillez corriger les erreurs dans le formulaire', { variant: 'error' });
      return;
    }
    
    setLoading(true);
    
    try {
      // Upload document image to Firebase Storage
      const storageRef = ref(storage, `verification_documents/${currentUser.uid}/${Date.now()}_${documentImage.name}`);
      await uploadBytes(storageRef, documentImage);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Create verification document in Firestore
      const verificationRef = doc(collection(db, 'identity_verifications'));
      await setDoc(verificationRef, {
        userId: currentUser.uid,
        documentType,
        documentImageUrl: downloadURL,
        documentData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          birthDate: formData.birthDate,
          nationality: formData.nationality,
          documentNumber: formData.documentNumber,
          expiryDate: formData.expiryDate
        },
        status: 'pending',
        submittedAt: serverTimestamp(),
        history: [
          {
            action: 'Soumission',
            date: serverTimestamp()
          }
        ]
      });
      
      // Update user document to indicate verification has been submitted
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        verificationSubmitted: true,
        verificationSubmittedAt: serverTimestamp(),
        verificationRejected: false,
        verificationRejectedReason: '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        nationality: formData.nationality
      });
      
      enqueueSnackbar('Votre demande de vérification a été soumise avec succès', { variant: 'success' });
      navigate('/verification/status');
    } catch (error) {
      console.error('Error submitting verification:', error);
      enqueueSnackbar('Erreur lors de la soumission de la vérification', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // If user is already verified
  if (userVerificationStatus?.isVerified) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <AlertTitle>Compte vérifié</AlertTitle>
            Votre compte a déjà été vérifié. Vous avez accès à toutes les fonctionnalités de l'application.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Retour au tableau de bord
          </Button>
        </Paper>
      </Box>
    );
  }
  
  // If verification is pending
  if (userVerificationStatus?.verificationSubmitted && !userVerificationStatus?.verificationRejected) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Vérification en cours</AlertTitle>
            Votre demande de vérification est en cours d'examen. Cette procédure peut prendre jusqu'à 24 heures ouvrables.
          </Alert>
          <Typography paragraph>
            Vous recevrez une notification dès que votre vérification sera traitée. En attendant, certaines fonctionnalités de l'application sont limitées.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Retour au tableau de bord
          </Button>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Vérification d'identité
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Pourquoi vérifier votre identité ?</AlertTitle>
          La vérification d'identité est nécessaire pour garantir la sécurité de tous les utilisateurs et accéder à toutes les fonctionnalités de l'application.
        </Alert>
        
        {userVerificationStatus?.verificationRejected && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Demande précédente rejetée</AlertTitle>
            Votre demande précédente a été rejetée pour la raison suivante : {userVerificationStatus.verificationRejectedReason}
            <br />
            Veuillez soumettre une nouvelle demande avec des documents valides.
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Document Type Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Type de document
              </Typography>
              <FormControl fullWidth error={!!errors.documentType}>
                <InputLabel>Type de document</InputLabel>
                <Select
                  value={documentType}
                  label="Type de document"
                  onChange={(e) => setDocumentType(e.target.value)}
                >
                  <MenuItem value="national_id">Carte d'identité nationale</MenuItem>
                  <MenuItem value="passport">Passeport</MenuItem>
                  <MenuItem value="driving_license">Permis de conduire</MenuItem>
                </Select>
                {errors.documentType && (
                  <Typography color="error" variant="caption">
                    {errors.documentType}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Document Upload */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Document d'identité
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  disabled={loading}
                >
                  Télécharger une image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {errors.documentImage && (
                  <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                    {errors.documentImage}
                  </Typography>
                )}
              </Box>
              
              {documentPreview && (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <img
                    src={documentPreview}
                    alt="Document preview"
                    style={{ maxWidth: '100%', maxHeight: '300px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <IconButton
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.7)' }}
                    onClick={() => {
                      setDocumentImage(null);
                      setDocumentPreview(null);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
              
              <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Conseils pour une vérification rapide :</strong>
                </Typography>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>Assurez-vous que le document est valide et non expiré</li>
                  <li>L'image doit être claire, bien éclairée et non floue</li>
                  <li>Tous les coins du document doivent être visibles</li>
                  <li>Les informations doivent être lisibles</li>
                </ul>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Informations du document
              </Typography>
            </Grid>
            
            {/* Personal Information */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de naissance"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleInputChange}
                error={!!errors.birthDate}
                helperText={errors.birthDate}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nationalité"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                error={!!errors.nationality}
                helperText={errors.nationality}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Numéro de document"
                name="documentNumber"
                value={formData.documentNumber}
                onChange={handleInputChange}
                error={!!errors.documentNumber}
                helperText={errors.documentNumber}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date d'expiration"
                name="expiryDate"
                type="date"
                value={formData.expiryDate}
                onChange={handleInputChange}
                error={!!errors.expiryDate}
                helperText={errors.expiryDate}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>
            
            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} color="inherit" />}
                >
                  {loading ? 'Soumission en cours...' : 'Soumettre pour vérification'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Confidentialité et sécurité
        </Typography>
        <Typography paragraph>
          Vos informations personnelles et documents d'identité sont traités de manière sécurisée et confidentielle, conformément à notre politique de confidentialité.
        </Typography>
        <Typography paragraph>
          Les documents que vous soumettez ne sont accessibles qu'au personnel autorisé et sont utilisés uniquement à des fins de vérification.
        </Typography>
      </Paper>
    </Box>
  );
};

export default SubmitVerificationPage;
